import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, FolderKanban, Shield, Mail, Receipt, Send,
  MoreVertical, KeyRound, UserX, UserCheck, Trash2, UserCog,
  Eye, Plus, AlertTriangle, Activity, LogIn, LogOut, UserPlus,
  RefreshCcw, Search, Megaphone, MailWarning, BarChart3, FileText,
} from "lucide-react";
import { http, setStoredToken } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import AdvertisingTab from "@/pages/AdvertisingTab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PLAN_OPTIONS = [
  { value: "free", label: "Free" },
  { value: "single", label: "Once-off (Single)" },
  { value: "pro", label: "Pro (admin grant)" },
  { value: "unlimited", label: "Unlimited (admin grant)" },
];

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "purchases", label: "Purchases", icon: Receipt },
  { id: "broadcast", label: "Broadcast", icon: Send },
  { id: "advertising", label: "Advertising", icon: Megaphone },
  { id: "email-failures", label: "Email failures", icon: MailWarning },
];

export default function Admin() {
  const navigate = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [savingUserId, setSavingUserId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [s, u] = await Promise.all([
        http.get("/admin/stats").then((r) => r.data),
        http.get("/admin/users").then((r) => r.data),
      ]);
      setStats(s);
      setUsers(u.users || []);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const updateUserPlan = async (userId, plan) => {
    setSavingUserId(userId);
    try {
      const { data } = await http.patch(`/admin/users/${userId}/plan`, { plan });
      const fresh = data?.user;
      setUsers((prev) => prev.map((u) =>
        u.user_id === userId
          ? { ...u, plan: fresh?.plan || plan, plan_status: fresh?.plan_status || u.plan_status }
          : u,
      ));
      const label = PLAN_OPTIONS.find((o) => o.value === plan)?.label || plan;
      toast.success(`Plan updated to ${label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update plan");
    } finally {
      setSavingUserId(null);
    }
  };

  const sendReset = async (u) => {
    try {
      const { data } = await http.post(`/admin/users/${u.user_id}/send-reset`);
      if (data.status === "sent") toast.success(`Reset email sent to ${u.email}`);
      else if (data.status === "not_sent") toast.warning("Reset link created but email not delivered (see logs)");
      else toast.error(`Reset email failed: ${data.status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset");
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "suspended" ? "active" : "suspended";
    try {
      const { data } = await http.patch(`/admin/users/${u.user_id}/status`, { status: next });
      setUsers((prev) => prev.map((x) =>
        x.user_id === u.user_id ? { ...x, status: data.user.status } : x,
      ));
      toast.success(next === "suspended" ? "User suspended" : "User reactivated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change status");
    }
  };

  const deleteUser = async (u) => {
    try {
      const { data } = await http.delete(`/admin/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success(`Deleted ${u.email} (${data.deleted.projects} project(s) cascaded)`);
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete user");
    }
  };

  const impersonate = async (u) => {
    try {
      // Backend returns the new impersonation JWT in the response body.
      // We MUST swap it into localStorage before refreshing — otherwise
      // the admin's existing Bearer token keeps flowing on subsequent
      // requests (cookies are stripped cross-origin in production), and
      // `/auth/me` would still resolve to the admin user.
      const { data } = await http.post(`/admin/users/${u.user_id}/impersonate`);
      if (data?.session_token) {
        setStoredToken(data.session_token);
      }
      await refreshUser?.();
      toast.success(`Now impersonating ${u.email}`);
      navigate("/projects");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not impersonate");
    }
  };

  return (
    <div className="min-h-screen fc-app-bg text-[#27272A] fc-fade-in" data-testid="admin-page">
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-back-btn"
              onClick={() => navigate("/projects")}
              className="w-8 h-8 rounded-lg border border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C] hover:border-black/[0.2] transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="fc-heading text-[#1C1C1C] text-lg font-medium">Admin</div>
              <div className="text-xs text-[#A8A29E] mt-0.5">Manage users, view payments, broadcast emails.</div>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-white border border-black/[0.08] rounded-lg p-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-colors ${
                    active ? "bg-sage-100 text-sage-700" : "text-[#57534E] hover:text-[#1C1C1C]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading && (
          <div className="text-center py-12 text-sm text-[#78716C]">Loading admin data…</div>
        )}

        {!loading && activeTab === "users" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <StatCard icon={Users} label="Total users" value={stats?.users ?? users.length} />
              <StatCard icon={FolderKanban} label="Total projects" value={stats?.projects ?? 0} />
              <StatCard
                icon={Shield}
                label="Paid plans"
                value={Object.entries(stats?.plans || {})
                  .filter(([plan]) => plan && plan !== "free")
                  .reduce((s, [, n]) => s + (n || 0), 0)}
              />
            </div>

            <div className="mt-8 fc-card" data-testid="admin-users-card">
              <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.06]">
                <div>
                  <div className="fc-label">Users</div>
                  <div className="fc-heading text-[#1C1C1C]">{users.length} total</div>
                </div>
                <Button
                  data-testid="admin-invite-btn"
                  onClick={() => setInviteOpen(true)}
                  className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3 text-sm font-medium inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Invite user
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="admin-users-table">
                  <thead className="bg-sage-50 text-sage-700">
                    <tr>
                      <Th>User</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Plan</Th>
                      <Th>Projects</Th>
                      <Th>Last active</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr
                        key={u.user_id}
                        className={`border-t border-black/[0.04] ${u.status === "suspended" ? "opacity-60" : ""}`}
                        data-testid={`admin-user-row-${u.user_id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3 min-w-0">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-black/[0.06]" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-sage-100 border border-sage-200 grid place-items-center text-sage-700 text-xs font-medium">
                                {(u.name || u.email || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-[#1C1C1C] font-medium truncate max-w-[220px]">{u.name || "—"}</div>
                              <div className="text-xs text-[#78716C] flex items-center gap-1 truncate max-w-[220px]">
                                <Mail className="w-3 h-3" /> {u.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4"><RoleBadge role={u.role} /></td>
                        <td className="py-3 px-4">
                          {u.status === "suspended" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700">
                              <AlertTriangle className="w-2.5 h-2.5" /> Suspended
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-sage-50 border border-sage-200 text-sage-700">Active</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {u.role === "admin" ? (
                            <span className="text-xs uppercase tracking-[0.15em] text-[#78716C]">{u.plan || "free"}</span>
                          ) : (
                            <select
                              data-testid={`admin-plan-select-${u.user_id}`}
                              value={u.plan || "free"}
                              disabled={savingUserId === u.user_id}
                              onChange={(e) => updateUserPlan(u.user_id, e.target.value)}
                              className="text-xs uppercase tracking-[0.15em] bg-white border border-sage-200 rounded-md px-2 py-1.5 text-[#1C1C1C] hover:border-sage-300 focus:outline-none focus:border-sage-400 disabled:opacity-50"
                            >
                              {PLAN_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="py-3 px-4 fc-mono text-[#1C1C1C]">{u.project_count ?? 0}</td>
                        <td className="py-3 px-4 text-xs text-[#57534E]">{fmtDate(u.last_login_at)}</td>
                        <td className="py-3 px-4 text-right">
                          {u.role !== "admin" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  data-testid={`admin-row-menu-${u.user_id}`}
                                  className="w-8 h-8 rounded-md border border-transparent hover:border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C]"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white text-[#27272A]">
                                <DropdownMenuItem onClick={() => sendReset(u)} data-testid={`admin-action-reset-${u.user_id}`}>
                                  <KeyRound className="w-4 h-4 mr-2" /> Send reset link
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => impersonate(u)} data-testid={`admin-action-impersonate-${u.user_id}`}>
                                  <UserCog className="w-4 h-4 mr-2" /> Impersonate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleStatus(u)} data-testid={`admin-action-status-${u.user_id}`}>
                                  {u.status === "suspended" ? (
                                    <><UserCheck className="w-4 h-4 mr-2" /> Reactivate</>
                                  ) : (
                                    <><UserX className="w-4 h-4 mr-2" /> Suspend</>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setConfirmDelete(u)}
                                  data-testid={`admin-action-delete-${u.user_id}`}
                                  className="text-red-600 focus:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete user…
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-10 text-sm text-[#A8A29E]">No users yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && activeTab === "activity" && <ActivityTab />}
        {!loading && activeTab === "analytics" && <AnalyticsTab />}
        {!loading && activeTab === "purchases" && <PurchasesTab />}
        {!loading && activeTab === "broadcast" && <BroadcastTab />}
        {!loading && activeTab === "advertising" && <AdvertisingTab />}
        {!loading && activeTab === "email-failures" && <EmailFailuresTab />}
      </main>

      <InviteUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => loadAll()}
      />

      <ConfirmDeleteDialog
        user={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteUser(confirmDelete)}
      />
    </div>
  );
}

// ---------------- helpers ----------------

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="fc-card p-5">
      <div className="flex items-center gap-2 fc-label text-sage-700">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="fc-heading text-3xl mt-2 text-[#1C1C1C]">{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left py-3 px-4 fc-label text-sage-700 whitespace-nowrap">{children}</th>;
}

function RoleBadge({ role }) {
  return role === "admin" ? (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1C1C1C] text-white">
      <Shield className="w-2.5 h-2.5" /> Admin
    </span>
  ) : (
    <span className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E]">User</span>
  );
}

// ---------------- Invite Dialog ----------------
function InviteUserDialog({ open, onOpenChange, onInvited }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("free");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await http.post("/admin/users", {
        email: email.trim(),
        name: name.trim() || undefined,
        plan,
        send_setup_email: sendEmail,
      });
      const status = data.setup_email_status;
      if (status === "sent") toast.success(`Invite sent to ${email}`);
      else if (status === "skipped") toast.success(`User created (no email sent)`);
      else toast.warning(`User created · email status: ${status}`);
      setEmail(""); setName(""); setPlan("free"); setSendEmail(true);
      onInvited?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not invite user");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-dialog" className="bg-white text-[#27272A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C]">Invite a user</DialogTitle>
          <DialogDescription className="text-xs text-[#78716C]">
            Adds the user immediately. If "Send setup email" is on, they'll receive a branded password-set link via Resend.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <Field label="Email">
            <Input data-testid="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
          </Field>
          <Field label="Name (optional)">
            <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" />
          </Field>
          <Field label="Plan">
            <select data-testid="invite-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="fc-input h-10 w-full rounded-md text-sm">
              {PLAN_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#27272A] cursor-pointer">
            <input data-testid="invite-send-email" type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
            Send branded setup email (password-set link)
          </label>
          <Button data-testid="invite-submit" type="submit" disabled={submitting} className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] w-full h-10 rounded-lg text-sm font-medium">
            {submitting ? "Inviting…" : "Send invite"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#A8A29E] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ---------------- Confirm Delete ----------------
function ConfirmDeleteDialog({ user, onCancel, onConfirm }) {
  return (
    <Dialog open={!!user} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent data-testid="delete-confirm-dialog" className="bg-white text-[#27272A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C] inline-flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600" /> Delete user?
          </DialogTitle>
          <DialogDescription className="text-xs text-[#78716C]">
            This permanently deletes <strong>{user?.email}</strong> and cascades all their projects, shares, and reset tokens. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mt-3">
          <Button variant="ghost" onClick={onCancel} className="flex-1">Cancel</Button>
          <Button data-testid="delete-confirm-btn" onClick={onConfirm} className="flex-1 bg-red-600 text-white hover:bg-red-700">
            Delete forever
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Purchases Tab ----------------
// ---------------- Activity Tab ----------------
const ACTIVITY_FILTERS = [
  { value: "", label: "All activity" },
  { value: "account_created", label: "Account created" },
  { value: "signed_in", label: "Sign-in" },
  { value: "signed_out", label: "Sign-out" },
  { value: "password_reset_requested", label: "Password reset requested" },
  { value: "password_reset_completed", label: "Password reset completed" },
  { value: "plan_changed", label: "Plan changed" },
  { value: "account_suspended", label: "Account suspended" },
  { value: "account_reactivated", label: "Account reactivated" },
  { value: "account_deleted", label: "Account deleted" },
  { value: "impersonated", label: "Impersonated" },
];

// Icon + colour palette per event so the table reads at a glance.
const ACTIVITY_DETAILS = {
  account_created:           { icon: UserPlus,   tone: "sage",   label: "Account created" },
  signed_in:                 { icon: LogIn,      tone: "sage",   label: "Signed in" },
  signed_out:                { icon: LogOut,     tone: "stone",  label: "Signed out" },
  password_reset_requested:  { icon: KeyRound,   tone: "amber",  label: "Password reset requested" },
  password_reset_completed:  { icon: KeyRound,   tone: "sage",   label: "Password reset completed" },
  plan_changed:              { icon: RefreshCcw, tone: "sage",   label: "Plan changed" },
  account_suspended:         { icon: UserX,      tone: "rose",   label: "Account suspended" },
  account_reactivated:       { icon: UserCheck,  tone: "sage",   label: "Account reactivated" },
  account_deleted:           { icon: Trash2,     tone: "rose",   label: "Account deleted" },
  impersonated:              { icon: UserCog,    tone: "amber",  label: "Impersonated" },
};

const ACTIVITY_TONE_CLASSES = {
  sage:  "bg-sage-100 text-sage-700 border-sage-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose:  "bg-rose-50 text-rose-700 border-rose-200",
  stone: "bg-stone-100 text-stone-700 border-stone-200",
};

function ActivityTab() {
  const [items, setItems] = useState(null);
  const [action, setAction] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (action) params.set("action", action);
      if (emailFilter.trim()) params.set("email", emailFilter.trim());
      const { data } = await http.get(`/admin/activity?${params}`);
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load activity log");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  return (
    <div data-testid="admin-activity-tab" className="space-y-5">
      <div className="fc-card p-4 flex flex-wrap items-end gap-3">
        <div className="grow min-w-[200px]">
          <div className="fc-label mb-1.5 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" /> Action
          </div>
          <select
            data-testid="activity-action-filter"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="fc-input h-10 w-full rounded-md text-sm px-2"
          >
            {ACTIVITY_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="grow min-w-[220px]">
          <div className="fc-label mb-1.5 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5" /> Email contains
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); load(); }}
            className="flex gap-2"
          >
            <Input
              data-testid="activity-email-filter"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="exact email — e.g. demo@user.com"
              className="fc-input h-10"
            />
            <Button
              type="submit"
              data-testid="activity-search-btn"
              variant="outline"
              className="h-10 px-3 border-black/[0.12]"
            >
              Search
            </Button>
          </form>
        </div>
        <Button
          type="button"
          onClick={load}
          variant="outline"
          data-testid="activity-refresh-btn"
          disabled={refreshing}
          className="h-10 px-3 border-black/[0.12] gap-1.5"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="fc-card">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-sage-50 text-sage-700">
              <tr>
                <Th>When</Th><Th>Action</Th><Th>User</Th><Th>IP</Th><Th>Actor</Th><Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {items === null && (
                <tr><td colSpan={6} className="text-center py-10 text-sm text-[#A8A29E]">Loading activity…</td></tr>
              )}
              {items !== null && items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-sm text-[#A8A29E]">No activity yet for this filter.</td></tr>
              )}
              {items !== null && items.map((row) => {
                const info = ACTIVITY_DETAILS[row.action] || { icon: Activity, tone: "stone", label: row.action };
                const IconEl = info.icon;
                const toneCls = ACTIVITY_TONE_CLASSES[info.tone] || ACTIVITY_TONE_CLASSES.stone;
                return (
                  <tr
                    key={row.id}
                    className="border-t border-black/[0.04]"
                    data-testid={`activity-row-${row.id}`}
                  >
                    <td className="py-3 px-4 text-xs text-[#57534E] whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border ${toneCls}`}>
                        <IconEl className="w-3 h-3" />
                        {info.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-[#1C1C1C] truncate max-w-[220px]">{row.email || "—"}</div>
                      <div className="text-[10px] text-[#A8A29E] fc-mono truncate max-w-[220px]">{row.user_id}</div>
                    </td>
                    <td className="py-3 px-4 text-xs text-[#78716C] fc-mono">{row.ip || "—"}</td>
                    <td className="py-3 px-4">
                      {row.actor_email ? (
                        <>
                          <div className="text-[#1C1C1C] text-xs truncate max-w-[180px]">{row.actor_email}</div>
                          <div className="text-[10px] text-amber-700 uppercase tracking-[0.15em]">admin</div>
                        </>
                      ) : <span className="text-[#A8A29E] text-xs">self</span>}
                    </td>
                    <td className="py-3 px-4 text-[11px] text-[#78716C] fc-mono max-w-[260px] truncate" title={JSON.stringify(row.meta)}>
                      {row.meta && Object.keys(row.meta).length
                        ? Object.entries(row.meta).map(([k, v]) => `${k}=${v}`).join(" · ")
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PurchasesTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get("/admin/purchases?limit=200").then((r) => setData(r.data)).catch(() => toast.error("Failed to load purchases"));
  }, []);
  if (!data) return <div className="text-center py-12 text-sm text-[#78716C]">Loading purchases…</div>;
  const symbol = (cur) => ({ ZAR: "R", USD: "$" }[cur] || "");
  return (
    <div data-testid="admin-purchases-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard icon={Receipt} label="Total purchases" value={data.total_purchases} />
        {Object.entries(data.revenue_by_currency || {}).map(([cur, info]) => (
          <StatCard
            key={cur}
            icon={Receipt}
            label={`Revenue (${cur})`}
            value={`${symbol(cur)}${(info.total_minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          />
        ))}
      </div>
      <div className="mt-8 fc-card">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-sage-50 text-sage-700">
              <tr>
                <Th>When</Th><Th>User</Th><Th>Plan</Th><Th>Amount</Th><Th>Reference</Th><Th>Slot grant</Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-black/[0.04]" data-testid={`purchase-row-${r.id}`}>
                  <td className="py-3 px-4 text-xs text-[#57534E]">{fmtDate(r.created_at)}</td>
                  <td className="py-3 px-4">
                    <div className="text-[#1C1C1C] font-medium truncate max-w-[220px]">{r.user_name || "—"}</div>
                    <div className="text-xs text-[#78716C] truncate max-w-[220px]">{r.user_email || r.user_id}</div>
                  </td>
                  <td className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-sage-700">{r.plan}</td>
                  <td className="py-3 px-4 fc-mono text-[#1C1C1C]">
                    {symbol(r.currency)}{r.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.currency}
                  </td>
                  <td className="py-3 px-4 text-xs text-[#78716C] fc-mono">{r.reference}</td>
                  <td className="py-3 px-4 text-xs">
                    {r.granted_extra_slot ? (
                      <span className="text-sage-700">+1 slot</span>
                    ) : <span className="text-[#A8A29E]">—</span>}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-sm text-[#A8A29E]">No purchases yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Broadcast Tab ----------------
const SEGMENTS = [
  { value: "all_users", label: "All active users" },
  { value: "waitlist", label: "Pricing waitlist" },
  { value: "users_with_projects", label: "Users with at least one project" },
  { value: "users_without_projects", label: "Users without any project (warm leads)" },
];

function BroadcastTab() {
  const [segment, setSegment] = useState("all_users");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async (testOnly) => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (!testOnly && !window.confirm(`Send to the "${SEGMENTS.find((s) => s.value === segment)?.label}" segment? This cannot be undone.`)) {
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await http.post("/admin/broadcast", {
        segment, subject, body_html: body, test_only: testOnly,
      });
      setLastResult(data);
      toast.success(testOnly
        ? `Test email sent to your inbox (${data.sent} sent · ${data.failed} failed)`
        : `Broadcast complete · ${data.sent} sent · ${data.failed} failed (of ${data.recipient_count} recipients)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-broadcast-tab">
      <div className="fc-card p-6">
        <div className="fc-label text-sage-700">Compose broadcast</div>
        <div className="fc-heading text-[#1C1C1C] mt-1.5">Send a branded email to a user segment</div>
        <p className="text-xs text-[#78716C] mt-1">
          Capped at 1,000 recipients per send. Use "Send test to me" first to preview the layout in your own inbox.
        </p>

        <div className="space-y-4 mt-5">
          <Field label="Segment">
            <select data-testid="broadcast-segment" value={segment} onChange={(e) => setSegment(e.target.value)} className="fc-input h-10 w-full rounded-md text-sm">
              {SEGMENTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
          </Field>
          <Field label="Subject">
            <Input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A short, scannable subject line" />
          </Field>
          <Field label="Body (HTML allowed)">
            <Textarea
              data-testid="broadcast-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="<p>Hi there — exciting news…</p><p>Your branded envelope (logo strip + footer) is added automatically.</p>"
              className="fc-input rounded-md font-mono text-[12px]"
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <Button data-testid="broadcast-test" onClick={() => send(true)} disabled={submitting} className="bg-white text-[#1C1C1C] border border-black/[0.12] hover:border-black/[0.28] hover:bg-[#F5F5F4] h-10 px-4 rounded-lg text-sm font-medium inline-flex items-center gap-1.5">
            <Eye className="w-4 h-4" /> Send test to me
          </Button>
          <Button data-testid="broadcast-send" onClick={() => send(false)} disabled={submitting} className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-10 px-4 rounded-lg text-sm font-medium inline-flex items-center gap-1.5">
            <Send className="w-4 h-4" /> Send to segment
          </Button>
        </div>

        {lastResult && (
          <div className="mt-5 text-[12px] text-[#57534E] bg-sage-50 border border-sage-200 rounded-lg p-3">
            <div><strong>Last result</strong> — segment: <em>{lastResult.segment}</em>{lastResult.test_only ? " (test)" : ""}</div>
            <div>Recipients: {lastResult.recipient_count} · Sent: {lastResult.sent} · Failed: {lastResult.failed}</div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Email failures tab — surfaces Resend send failures so admins can see why
// an invite or password reset never landed (quota exceeded, unverified
// domain, bounced address, etc.).
// ---------------------------------------------------------------------------
function EmailFailuresTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await http.get("/admin/email-failures?limit=200");
      setRows(data.items || []);
    } catch {
      toast.error("Failed to load email failures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Clear the entire email failure log? This cannot be undone.")) return;
    try {
      setClearing(true);
      const { data } = await http.delete("/admin/email-failures");
      toast.success(`Cleared ${data.deleted ?? 0} entries`);
      setRows([]);
    } catch {
      toast.error("Failed to clear log");
    } finally {
      setClearing(false);
    }
  };

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-email-failures-tab" className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]">Email failures</h2>
          <p className="text-[12px] text-[#78716C] mt-1 max-w-xl">
            Resend send attempts that failed or were skipped (quota exceeded, unverified domain, bounced address, missing API key). Only failures land here — successful sends aren't logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="email-failures-refresh-btn"
            onClick={load}
            variant="ghost"
            className="h-9 text-sm text-[#57534E] hover:text-[#1C1C1C]"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button
            data-testid="email-failures-clear-btn"
            onClick={clearAll}
            disabled={clearing || rows.length === 0}
            variant="ghost"
            className="h-9 text-sm text-[#B91C1C] hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear log
          </Button>
        </div>
      </div>

      <div className="fc-card p-0 overflow-hidden">
        <div className="overflow-x-auto fc-no-scrollbar">
          <table className="w-full min-w-[720px] text-sm" data-testid="email-failures-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#A8A29E] border-b border-black/[0.06]">
                <th className="py-3 px-5 font-normal w-44">When</th>
                <th className="py-3 px-2 font-normal w-24">Status</th>
                <th className="py-3 px-2 font-normal">Recipient</th>
                <th className="py-3 px-2 font-normal">Subject</th>
                <th className="py-3 px-5 font-normal">Reason</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="text-center py-10 text-sm text-[#A8A29E]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-sm text-[#A8A29E]" data-testid="email-failures-empty">No email failures logged. Everything's been delivering cleanly.</td></tr>
              )}
              {!loading && rows.map((r, idx) => (
                <tr
                  key={`${r.created_at}-${idx}`}
                  className="border-t border-black/[0.04]"
                  data-testid={`email-failure-row-${idx}`}
                >
                  <td className="py-3 px-5 text-[#57534E] fc-mono text-[11px] align-top whitespace-nowrap">{formatTime(r.created_at)}</td>
                  <td className="py-3 px-2 align-top">
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.15em] rounded-full px-2 py-0.5 border ${
                      r.status === "failed"
                        ? "bg-red-50 border-red-200 text-red-700"
                        : "bg-amber-50 border-amber-200 text-amber-700"
                    }`}>{r.status}</span>
                  </td>
                  <td className="py-3 px-2 text-[#1C1C1C] align-top break-all">
                    {(r.to || []).join(", ") || "—"}
                  </td>
                  <td className="py-3 px-2 text-[#1C1C1C] align-top">{r.subject || "—"}</td>
                  <td className="py-3 px-5 text-[#57534E] align-top text-[12px] leading-snug break-all">{r.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Analytics tab — in-house, cookie-free page-view dashboard. Fed by
// `usePageViewTracker` on the frontend + `/api/admin/analytics/traffic`
// on the backend. Shows today/week/month tiles, a 30-day trend strip,
// top pages, and top referrers.
// ---------------------------------------------------------------------------
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await http.get("/admin/analytics/traffic");
      setData(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Pad missing days with zeros so the trend strip never looks "gappy".
  const dailyPadded = (() => {
    const map = new Map((data?.daily || []).map((d) => [d.date, d]));
    const out = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push(map.get(key) || { date: key, views: 0, visitors: 0 });
    }
    return out;
  })();
  const maxViews = Math.max(1, ...dailyPadded.map((d) => d.views));

  const fmtPath = (p) => (p && p.length > 40 ? p.slice(0, 40) + "…" : p || "/");
  const fmtRef = (r) => {
    if (!r || r === "Direct") return "Direct";
    try { return new URL(r).hostname || r; } catch { return r.slice(0, 60); }
  };

  return (
    <div data-testid="admin-analytics-tab" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]">Traffic & visitors</h2>
          <p className="text-[12px] text-[#78716C] mt-1 max-w-xl">
            Cookie-free page-views collected directly from the frontend. Admin pages, known bots, and duplicate fires within 1.5s are filtered. Data retained 90 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="analytics-report-btn"
            onClick={() => window.open("/admin/analytics/report", "_blank")}
            variant="ghost"
            className="h-9 text-sm text-[#57534E] hover:text-[#1C1C1C]"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Generate report
          </Button>
          <Button
            data-testid="analytics-refresh-btn"
            onClick={load}
            variant="ghost"
            className="h-9 text-sm text-[#57534E] hover:text-[#1C1C1C]"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {["day", "week", "month"].map((bucket) => (
          <div key={bucket} className="fc-card p-4" data-testid={`analytics-tile-${bucket}`}>
            <div className="fc-label">Last {bucket === "day" ? "24h" : bucket === "week" ? "7 days" : "30 days"}</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">Visitors</div>
                <div className="fc-mono text-xl text-[#1C1C1C] mt-0.5">
                  {loading ? "—" : (data?.totals?.visitors?.[bucket] ?? 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">Views</div>
                <div className="fc-mono text-xl text-[#1C1C1C] mt-0.5">
                  {loading ? "—" : (data?.totals?.views?.[bucket] ?? 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 30-day trend strip */}
      <div className="fc-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="fc-label">Last 30 days · views per day</div>
          <div className="text-[11px] text-[#A8A29E]">Peak: {maxViews.toLocaleString()}</div>
        </div>
        <div className="flex items-end gap-1 h-24" data-testid="analytics-trend-strip">
          {dailyPadded.map((d) => {
            const pct = (d.views / maxViews) * 100;
            return (
              <div
                key={d.date}
                className="flex-1 bg-sage-200 hover:bg-sage-400 rounded-sm transition-colors min-h-[2px]"
                style={{ height: `${Math.max(2, pct)}%` }}
                title={`${d.date} · ${d.views} views · ${d.visitors} visitors`}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-[#A8A29E] mt-2 fc-mono">
          <span>{dailyPadded[0]?.date}</span>
          <span>{dailyPadded[dailyPadded.length - 1]?.date}</span>
        </div>
      </div>

      {/* Top pages + Top referrers + Device donut */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="fc-card p-0 overflow-hidden md:col-span-1">
          <div className="px-5 py-3 border-b border-black/[0.06] flex items-center justify-between">
            <div className="fc-label">Devices · 30d</div>
            <div className="text-[11px] text-[#A8A29E]">{(data?.devices || []).reduce((s, d) => s + d.views, 0).toLocaleString()} views</div>
          </div>
          <DeviceDonut devices={data?.devices || []} loading={loading} />
        </div>
        <div className="fc-card p-0 overflow-hidden md:col-span-1">
          <div className="px-5 py-3 border-b border-black/[0.06] flex items-center justify-between">
            <div className="fc-label">Top pages · 30d</div>
            <div className="text-[11px] text-[#A8A29E]">{(data?.top_pages || []).length} of 15</div>
          </div>
          <table className="w-full text-sm" data-testid="analytics-top-pages">
            <tbody>
              {(data?.top_pages || []).map((p) => (
                <tr key={p.path} className="border-t border-black/[0.04]">
                  <td className="py-2.5 px-5 text-[#1C1C1C] truncate max-w-[200px]" title={p.path}>{fmtPath(p.path)}</td>
                  <td className="py-2.5 px-5 text-right fc-mono text-[#1C1C1C]">{p.views.toLocaleString()}</td>
                </tr>
              ))}
              {(!loading && (data?.top_pages || []).length === 0) && (
                <tr><td colSpan={2} className="text-center py-8 text-sm text-[#A8A29E]">No page views yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="fc-card p-0 overflow-hidden md:col-span-1">
          <div className="px-5 py-3 border-b border-black/[0.06] flex items-center justify-between">
            <div className="fc-label">Top referrers · 30d</div>
            <div className="text-[11px] text-[#A8A29E]">{(data?.top_referrers || []).length} of 15</div>
          </div>
          <table className="w-full text-sm" data-testid="analytics-top-referrers">
            <tbody>
              {(data?.top_referrers || []).map((r) => (
                <tr key={r.referrer} className="border-t border-black/[0.04]">
                  <td className="py-2.5 px-5 text-[#1C1C1C] truncate max-w-[200px]" title={r.referrer}>{fmtRef(r.referrer)}</td>
                  <td className="py-2.5 px-5 text-right fc-mono text-[#1C1C1C]">{r.views.toLocaleString()}</td>
                </tr>
              ))}
              {(!loading && (data?.top_referrers || []).length === 0) && (
                <tr><td colSpan={2} className="text-center py-8 text-sm text-[#A8A29E]">No referrers tracked yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Simple SVG donut showing the share of page-views by device class.
 * Built in plain SVG (no chart lib) — sage palette to match the rest of
 * the Admin UI.
 */
function DeviceDonut({ devices, loading }) {
  const total = devices.reduce((s, d) => s + d.views, 0);
  // Stable palette: Mobile = sage 700, Desktop = sage 400, Tablet = olive, Unknown = grey.
  const COLOURS = {
    Mobile: "#4A5C3E",
    Desktop: "#A8BA99",
    Tablet: "#A8B470",
    Unknown: "#D6D3D1",
  };
  if (loading) {
    return <div className="text-center py-10 text-sm text-[#A8A29E]" data-testid="device-donut-loading">Loading…</div>;
  }
  if (total === 0) {
    return <div className="text-center py-10 text-sm text-[#A8A29E]" data-testid="device-donut-empty">No device data yet.</div>;
  }
  // Polar-to-cartesian helper for arc paths.
  const R = 70;
  const STROKE = 22;
  const CIRC = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="p-5 flex items-center gap-5" data-testid="analytics-device-donut">
      <svg width="180" height="180" viewBox="-10 -10 180 180">
        <circle
          cx="80" cy="80" r={R}
          fill="none" stroke="#F4F4F2" strokeWidth={STROKE}
        />
        {devices.map((d) => {
          const frac = d.views / total;
          const len = frac * CIRC;
          const dash = `${len} ${CIRC - len}`;
          const seg = (
            <circle
              key={d.device}
              cx="80" cy="80" r={R}
              fill="none"
              stroke={COLOURS[d.device] || "#D6D3D1"}
              strokeWidth={STROKE}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              transform="rotate(-90 80 80)"
            />
          );
          offset += len;
          return seg;
        })}
        <text
          x="80" y="78"
          textAnchor="middle"
          className="fc-mono"
          fontSize="20"
          fill="#1C1C1C"
        >
          {total.toLocaleString()}
        </text>
        <text
          x="80" y="96"
          textAnchor="middle"
          fontSize="9"
          fill="#A8A29E"
          letterSpacing="2"
        >
          VIEWS
        </text>
      </svg>
      <ul className="space-y-1.5 text-[12px] flex-1">
        {devices.map((d) => {
          const pct = ((d.views / total) * 100).toFixed(0);
          return (
            <li key={d.device} className="flex items-center gap-2" data-testid={`device-row-${d.device.toLowerCase()}`}>
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ background: COLOURS[d.device] || "#D6D3D1" }}
              />
              <span className="text-[#1C1C1C] flex-1">{d.device}</span>
              <span className="fc-mono text-[#57534E]">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

